import { exploreSchemaItemList } from '@/templates/Landing/seoLandings/exploreSchema';
import { ENV } from '@/constants';

export const schemaObject_AiPokemonGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-pokemon-generator',
			name: 'AI Pokémon Generator — Custom Pokémon AI Art Creator | Lift',
			description:
				"Create custom Pokemon AI art from any photo with Lift's AI Pokemon generator. No watermark. Upload a photo, apply a filter, and transform yourself, team, or pet into unique Pokémon-style characters.",
			url: 'https://liftapp.ai/ai-pokemon-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-pokemon-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#product',
			name: 'AI Pokémon Generator',
			description:
				"Create custom Pokemon AI art from any photo with Lift's AI Pokemon generator. Transform yourself, team, or pet into unique Pokémon-style characters — no drawing skills needed.",
			url: 'https://liftapp.ai/ai-pokemon-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: 'Select a photo from your gallery or snap one directly in the app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'AI creates your custom Pokémon',
					text: 'Our AI analyzes your photo and transforms it into an original Pokémon-style character',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Apply the transformation',
					text: 'Let AI reimagine your photo in seconds',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and share',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#benefits',
			name: 'Who can benefit from AI Pokémon Generator?',
			description:
				"Lift's AI Pokémon Generator is perfect for fans, pet owners, content creators, and anyone who wants to create unique Pokémon-style artwork from their photos.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Pokémon fans can see themselves reimagined as custom characters in their favorite art style',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Pet owners can transform their pets into adorable Pokémon creatures with unique types and features',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators can generate viral-worthy Pokémon team art for TikTok, Instagram, and YouTube',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Friends and families can create matching Pokémon team art for group chats and social posts',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Quick, creative, and premium-quality — ideal for personal fun',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Pokémon Generator by Lift',
				description:
					'Example of how Lift AI Pokémon Generator transforms photos into custom Pokémon-style characters.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Pokémon Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Pokémon Generator is an online tool that transforms your photos into custom Pokémon-style artwork — perfect for profile pictures, fan art, memes, or sharing with friends who love the franchise.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Pokémon Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI analyzes your photo with deep learning, identifies key features and characteristics, and reimagines them in authentic Pokémon art style with custom colors, traits, and backgrounds. Everything is automatic — no manual editing or drawing skills needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I turn my pet into a Pokémon?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes! Lift works perfectly with pet photos. Upload a picture of your dog, cat, or any pet, and the AI will transform it into a custom Pokémon character with features that match your pet's unique appearance and personality.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, stickers, or phone wallpapers.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#why-lift',
			name: "Why Lift's AI Pokémon Generator stands out",
			description:
				"Lift's AI Pokémon Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Pokémon Generator',
				description:
					"Comparison of Lift's AI Pokémon Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-pokemon-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Pokémon Generator',
					item: 'https://liftapp.ai/ai-pokemon-generator',
				},
			],
		},
	],
};
